/**
 * ListModel — 枚举/下拉列表数据模型
 *
 * ## 架构角色
 * 下拉框、单选/多选枚举、标签等列表型控件在运行时对应一个 ListModel 实例。
 * 继承 BaseModel，负责列表数据源管理、选中项维护、值读写与校验。
 *
 * ## 模型配置（new ttx.models.ListModel(options)）
 * | 配置项 | 类型 | 默认值 | 说明 |
 * |--------|------|--------|------|
 * | value          | any     | null   | 当前值 |
 * | multiple       | Boolean | false  | 是否多选 |
 * | allowClear     | Boolean | true   | 是否允许清空 |
 * | valueField     | String  | 'value'| 值字段名 |
 * | textField      | String  | 'text' | 文本字段名 |
 * | dataSource     | Array   | []     | 本地数据源 |
 * | dataSourceMode | string  | 'local'| 数据源模式，其它为网络请求 |
 * | keyMap         | Object  | {}     | key 映射表 |
 * | autoComplete   | Boolean | false  | 是否自动补全 |
 *
 * ## 事件（6 个，通过 on/execute 触发）
 * | 事件 | 说明 |
 * |------|------|
 * | beforeSetDataSource | 设置数据源前；返回 false 中止 |
 * | afterSetDataSource  | 设置数据源后 |
 * | beforeSelect        | 列表项选择前；返回 false 中止 |
 * | afterSelect         | 列表项选择后 |
 * | beforeValueChange   | 值变更前；返回 false 中止 |
 * | afterValueChange    | 值变更后 |
 *
 * ## 使用示例
 * ```js
 * var listModel = new ttx.models.ListModel({
 *   cItemName: 'status', cControlType: 'select',
 *   valueField: 'value', textField: 'text',
 *   dataSource: [{ value: 1, text: '启用' }, { value: 0, text: '停用' }]
 * });
 * listModel.setValue(1);
 * listModel.on('afterValueChange', function(data) { console.log(data.value); });
 * ```
 */
ttx.models.register('ListModel', function (modelType) {
    // 默认配置
    const DEFAULTS = {
        value: null,
        multiple: false,
        allowClear: true,
        valueField: 'value',
        textField: 'text',
        dataSource: [],
        dataSourceMode: 'local',
        keyMap: {},
        autoComplete: false
    };

    const listModel = function (data) {
        // 合并默认配置
        const merged = ttx.utils.extend({}, DEFAULTS, data || {});
        ttx.models.BaseModel.call(this, merged);
        // 选中项 key 缓存
        this.setCache('selectedKeys', []);
    };
    listModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
    listModel.prototype.modelType = modelType;

    // ===== 内部工具 =====

    // 获取数据源
    listModel.prototype._getDataSource = function () {
        return this._get_data('dataSource') || [];
    };

    // 获取节点的 key（valueField 值）
    listModel.prototype._getNodeKey = function (node) {
        if (!node) return null;
        const valueField = this._get_data('valueField') || 'value';
        return node[valueField];
    };

    // 通知监听器值变化
    listModel.prototype._notifyValueChange = function (value) {
        this.doPropertyChange('setState', { value: value });
    };

    // 远程数据拉取
    listModel.prototype._fetchRemote = function (proxyConfig, queryParams, callback) {
        const self = this;
        const url = (typeof proxyConfig === 'string') ? proxyConfig : (proxyConfig && proxyConfig.url);
        if (!url) {
            if (typeof callback === 'function') callback([]);
            return;
        }
        ttx.ajax.xhrUtil({
            url: url,
            method: (proxyConfig && proxyConfig.method) || 'get',
            data: queryParams || {},
            success: function (res) {
                const list = (res && res.data && (res.data.list || res.data.data)) || (res && res.data) || [];
                const rows = ttx.utils.isArray(list) ? list : [];
                if (typeof callback === 'function') callback(rows);
            },
            fail: function () {
                if (typeof callback === 'function') callback([]);
            }
        });
    };

    // ===== 值读写 =====

    /**
     * 设置模型的值 value
     * @param {*} data 值
     */
    listModel.prototype.setData = function (data) {
        this.setValue(data);
    };

    /**
     * 获取模型的值 value
     * @returns {*} 当前值
     */
    listModel.prototype.getData = function () {
        return this._get_data('value');
    };

    /**
     * 清空，将 propertyNames 的值更新到当前组件
     * @param {Boolean} useDefault true 恢复默认值，false 置空
     */
    listModel.prototype.clear = function (useDefault) {
        const propertyNames = this._get_data('propertyNames') || [];
        const self = this;
        propertyNames.forEach(function (name) {
            if (name === 'value') return; // value 单独处理
            self._set_data(name, null, true);
        });
        if (useDefault === false) {
            this.setValue(null);
        } else {
            // 恢复默认值
            const defaultValue = this._get_data('cDefaultValue');
            this.setValue(defaultValue != null ? defaultValue : null);
        }
        // 通知组件更新
        this.doPropertyChange('setState', { value: this._get_data('value') });
    };

    /**
     * 设置数据源
     * - local 模式：proxyConfig 传真实数据数组
     * - 远程模式：proxyConfig 传服务地址，queryParams 传请求参数，callback 回调
     * 触发 beforeSetDataSource / afterSetDataSource 事件；before 返回 false 中止
     */
    listModel.prototype.setDataSource = function (proxyConfig, queryParams, callback) {
        const data = { proxyConfig: proxyConfig, queryParams: queryParams, callback: callback };
        // beforeSetDataSource：返回 false 中止
        if (this.execute('beforeSetDataSource', data) === false) return;

        if (ttx.utils.isArray(proxyConfig)) {
            // local 模式，proxyConfig 为真实数据
            this._set_data('dataSource', proxyConfig, true);
            this.execute('afterSetDataSource', data);
            if (typeof callback === 'function') callback(proxyConfig);
        } else {
            // 远程模式
            const self = this;
            this._fetchRemote(proxyConfig, queryParams, function (rows) {
                self._set_data('dataSource', rows, true);
                self.execute('afterSetDataSource', data);
                if (typeof callback === 'function') callback(rows);
            });
        }
        // 通知监听器数据源变化
        this.doPropertyChange('setState', { dataSource: this._get_data('dataSource') });
    };

    /**
     * 根据 keys（valueField 值）获取节点数据
     * @param {Array|*} keys key 数组（单个 key 也可）
     * @returns {Array} 节点数据数组
     */
    listModel.prototype.getNodesByKeys = function (keys) {
        const keyArr = ttx.utils.isArray(keys) ? keys : (keys != null ? [keys] : []);
        const dataSource = this._getDataSource();
        const valueField = this._get_data('valueField') || 'value';
        const result = [];
        keyArr.forEach(function (k) {
            const node = dataSource.find(function (item) { return item[valueField] === k; });
            if (node) result.push(node);
        });
        return result;
    };

    // ===== 选择 =====

    /**
     * 列表数据项选择事件
     * @param {Array|*} selectedKeys 选中的 key 数组（单个 key 也可）
     * @param {Boolean} manually 是否手动触发
     * 触发 beforeSelect / afterSelect 事件；before 返回 false 中止
     */
    listModel.prototype.select = function (selectedKeys, manually) {
        const keys = ttx.utils.isArray(selectedKeys) ? selectedKeys : (selectedKeys != null ? [selectedKeys] : []);
        const data = { selectedKeys: keys, manually: manually };
        // beforeSelect：返回 false 中止
        if (this.execute('beforeSelect', data) === false) return;
        this.setCache('selectedKeys', keys);
        // 多选时值为数组，单选时取第一个
        const multiple = this._get_data('multiple');
        const value = multiple ? keys : (keys.length ? keys[0] : null);
        this.setValue(value);
        this.execute('afterSelect', data);
    };

    /**
     * 获取选中的数据项
     * @returns {Array} 节点数据数组
     */
    listModel.prototype.getSelectedNodes = function () {
        const keys = this.getCache('selectedKeys') || [];
        return this.getNodesByKeys(keys);
    };

    /**
     * 获取选中的数据项的 key
     * @returns {Array} key 数组
     */
    listModel.prototype.getSelectedKeys = function () {
        return this.getCache('selectedKeys') || [];
    };

    // ===== 值设置 =====

    /**
     * 设置值
     * @param {*} value  新值
     * @param {Boolean} check 是否触发前置校验
     * 触发 beforeValueChange / afterValueChange 事件；before 返回 false 中止
     */
    listModel.prototype.setValue = function (value, check) {
        const oldValue = this._get_data('value');
        const data = { value: value, oldValue: oldValue };
        // beforeValueChange：返回 false 中止
        if (check && this.execute('beforeValueChange', data) === false) return;
        this._set_data('value', value, true);
        // 同步 selectedKeys
        const multiple = this._get_data('multiple');
        if (value != null) {
            this.setCache('selectedKeys', multiple ? value : [value]);
        } else {
            this.setCache('selectedKeys', []);
        }
        // 通知监听器
        this._notifyValueChange(value);
        // check=true 时触发 afterValueChange 事件（与 SimpleModel 保持一致）
        check && this.execute('afterValueChange', data);
    };

    /**
     * 设置 prevvalue（用于变更历史对比）
     * @param {*} value 上一次的值
     */
    listModel.prototype.setPrevValue = function (value) {
        this._set_data('prevvalue', value, true);
        this.doPropertyChange('setState', { prevvalue: value });
    };

    /**
     * 获取值
     * @returns {*} 当前值
     */
    listModel.prototype.getValue = function () {
        return this._get_data('value');
    };

    return listModel;
});
